<script setup lang="ts">
import { UsersRound, Loader2 } from 'lucide-vue-next';
import { ref, watch, computed } from 'vue';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAccessManagementStore } from '@/stores/access-management';

interface Props {
  orgId: string;
  orgName: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  success: [];
}>();

const accessStore = useAccessManagementStore();

const isOpen = ref(false);
const email = ref('');
const role = ref<'MEMBER' | 'VIEWER'>('MEMBER');
const isInviting = ref(false);

watch(isOpen, (open) => {
  if (open) {
    // Reset form when dialog opens
    email.value = '';
    role.value = 'MEMBER';
    isInviting.value = false;
  }
});

const canInvite = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email.value && emailRegex.test(email.value) && role.value && !isInviting.value;
});

async function handleInvite() {
  if (!canInvite.value) {return;}

  try {
    isInviting.value = true;
    await accessStore.inviteMember(props.orgId, email.value, role.value);

    // Delay 300ms for better UX
    await new Promise(resolve => setTimeout(resolve, 300));

    isOpen.value = false;
    emit('success');
  } catch (error) {
    // Error already handled by store (toast notification)
    console.error('Failed to invite member:', error);
  } finally {
    isInviting.value = false;
  }
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot name="trigger">
        <Button size="sm" class="gap-2">
          <UsersRound class="h-4 w-4" />
          Invite Member
        </Button>
      </slot>
    </DialogTrigger>

    <DialogContent class="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogDescription>
          Add someone to {{ orgName }}
        </DialogDescription>
      </DialogHeader>

      <div class="grid gap-4 py-4">
        <!-- Email Input -->
        <div class="grid gap-2">
          <Label for="email">
            Email
            <span class="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            v-model="email"
            type="email"
            placeholder="user@example.com"
            :disabled="isInviting"
            @keydown.enter="handleInvite"
          />
        </div>

        <!-- Role Selection -->
        <div class="grid gap-2">
          <Label for="role">
            Role
            <span class="text-destructive">*</span>
          </Label>
          <Select v-model="role" :disabled="isInviting">
            <SelectTrigger id="role">
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MEMBER">
                <div class="flex flex-col">
                  <span class="font-medium">Member</span>
                  <span class="text-muted-foreground text-xs">Full access to organization data</span>
                </div>
              </SelectItem>
              <SelectItem value="VIEWER">
                <div class="flex flex-col">
                  <span class="font-medium">Viewer</span>
                  <span class="text-muted-foreground text-xs">Read-only access</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Info Box -->
        <div class="bg-muted rounded-md p-3 text-sm">
          <p class="text-muted-foreground text-xs">
            The system will automatically create an account if the user doesn't exist.
            They will receive access to {{ orgName }} with {{ role }} permissions.
          </p>
        </div>

      </div>

      <DialogFooter>
        <Button
          variant="outline"
          @click="isOpen = false"
          :disabled="isInviting"
        >
          Cancel
        </Button>
        <Button
          @click="handleInvite"
          :disabled="!canInvite"
        >
          <Loader2 v-if="isInviting" class="mr-2 h-4 w-4 animate-spin" />
          {{ isInviting ? 'Sending...' : 'Send Invitation' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
